import type { ProspectResponse } from '@common/types/prospects';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import {
    Box,
    Button,
    Divider,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Skeleton,
    Stack,
    Step,
    StepLabel,
    Stepper,
    Typography,
    useTheme,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import FormComponent from '@/components/FormComponent/FormComponent';
import LexoSwitch from '@/components/LexoSwitch';
import { STATUS_OPTIONS } from '@/constants';
import {
    buildSummaryDynamicFields,
    prospectDetailsQueryKey,
    useIntakeEmailReminder,
    useQuoteEmailReminder,
} from '@/queries/prospectDetails';
import { useQuoteTemplateFormFields } from '@/queries/templates';
import { patchProspectById } from '@/services/ProspectService';
import { errorSnackbar, warningSnackbar } from '@/utils/snackbar';
import {
    applyContactUpdate,
    applyProspectFieldUpdate,
    mergePatchResponse,
    toPatchPath,
    toPatchValue,
} from '../prospectDetailsState';
import SendManualQuoteModal from '../SendManualQuoteModal/SendManualQuoteModal';
import PrivateLenderPricingBreakdown from './PricingBreakdown/PrivateLenderPricingBreakdown';
import PurchasePricingBreakdown from './PricingBreakdown/PurchasePricingBreakdown';
import RefinancePricingBreakdown from './PricingBreakdown/RefinancePricingBreakdown';
import SalePricingBreakdown from './PricingBreakdown/SalePricingBreakdown';
import PrivateLenderPricingInput from './PricingInputs/PrivateLenderPricingInput';
import PurchasePricingInput from './PricingInputs/PurchasePricingInput';
import RefinancePricingInput from './PricingInputs/RefinancePricingInput';
import SalePricingInput from './PricingInputs/SalePricingInput';
import SaleProspectInformation from './SaleProspectInformation';

interface Props {
    prospectId?: string;
    prospect: ProspectResponse;
    selectedVersionId: number | null;
}

export default function ProspectSummary({ prospectId, prospect, selectedVersionId }: Props) {
    const theme = useTheme();
    const queryClient = useQueryClient();
    const [isPatchInProgress, setIsPatchInProgress] = useState(false);
    const [isSendQuoteModalOpen, setIsSendQuoteModalOpen] = useState(false);

    const { data: quoteFormFields = [], isPending: isQuoteFormFieldsPending } =
        useQuoteTemplateFormFields(prospect.quoteTemplateId);
    const { data: quoteReminder } = useQuoteEmailReminder(prospect.quoteTemplateId);
    const { data: intakeReminder } = useIntakeEmailReminder(prospect.quoteTemplateId);

    const needsQuoteFormFields =
        prospect.matterType === 'Sale' || prospect.matterType === 'Purchase';
    const isLoadingQuoteFields = !!prospect.quoteTemplateId && isQuoteFormFieldsPending;

    const summaryDynamicFields = useMemo(
        () => buildSummaryDynamicFields(quoteFormFields, prospect.clientQuoteResponse),
        [quoteFormFields, prospect.clientQuoteResponse],
    );

    const updateProspectInCache = (updater: (prev: ProspectResponse) => ProspectResponse) => {
        queryClient.setQueryData(
            prospectDetailsQueryKey(prospectId, selectedVersionId),
            (prev: ProspectResponse | undefined) => (prev ? updater(prev) : prev),
        );
    };

    const handleValueChange = async (name: string, value: any) => {
        if (name === 'firstName' || name === 'middleName' || name === 'lastName') {
            updateProspectInCache((prev) => {
                const updated = applyContactUpdate(prev, name, value);
                let fullName = '';
                if (name === 'firstName') {
                    fullName = `${prev?.contact?.lastName ?? ''}, ${value} ${
                        prev?.contact?.middleName ?? ''
                    }`;
                }
                if (name === 'middleName') {
                    fullName = `${prev?.contact?.lastName ?? ''}, ${
                        prev?.contact?.firstName ?? ''
                    } ${value}`;
                }
                if (name === 'lastName') {
                    fullName = `${value}, ${prev?.contact?.firstName ?? ''} ${
                        prev?.contact?.middleName ?? ''
                    }`;
                }
                return applyContactUpdate(updated, 'contactName', fullName);
            });
            return;
        }

        const patchValue = toPatchValue(name, value);
        updateProspectInCache((prev) => applyProspectFieldUpdate(prev, name, patchValue));
        setIsPatchInProgress(true);

        try {
            const response = await patchProspectById(prospectId as string, [
                {
                    op: 'replace',
                    path: toPatchPath(name),
                    value: patchValue,
                },
            ]);
            queryClient.setQueryData(
                prospectDetailsQueryKey(prospectId, selectedVersionId),
                (prev: ProspectResponse | undefined) =>
                    prev ? mergePatchResponse(prev, response, name) : response,
            );
        } catch (error) {
            console.error('API request error:', error);
            warningSnackbar('Please refresh the page!');
            await queryClient.invalidateQueries({
                queryKey: ['prospect', prospectId],
            });
        } finally {
            setIsPatchInProgress(false);
        }
    };

    const handleReminderChange = async (reminderType: string) => {
        try {
            const payload = {
                name: '',
                value: false,
            };
            if (reminderType === 'quote') {
                payload.name = 'isQuoteReminderEnabled';
                payload.value = !prospect.isQuoteReminderEnabled;
            } else if (reminderType === 'intake') {
                payload.name = 'isIntakeReminderEnabled';
                payload.value = !prospect.isIntakeReminderEnabled;
            }
            await handleValueChange(payload.name, payload.value);
        } catch (error) {
            console.error(`Error updating ${reminderType} email reminder:`, error);
            errorSnackbar(`Error updating ${reminderType} email reminder`);
        }
    };

    const renderProspectInformation = () => {
        const { matterType } = prospect;
        const supportedTypes = ['Sale', 'Purchase', 'Refinance', 'PrivateLender'];

        if (supportedTypes.includes(matterType)) {
            return (
                <SaleProspectInformation
                    prospectId={prospectId}
                    prospect={prospect}
                    onProspectChange={(updated) =>
                        queryClient.setQueryData(
                            prospectDetailsQueryKey(prospectId, selectedVersionId),
                            updated,
                        )
                    }
                    handleValueChange={handleValueChange}
                    readOnly
                />
            );
        }

        return null;
    };

    const steps = ['Quote requested', 'Quote sent', 'Intake sent', 'Intake completed'];

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Box
                sx={{
                    display: 'flex',
                    gap: '24px',
                    boxSizing: 'border-box',
                    flexDirection: 'column',
                    width: 'calc(100% - 48px)',
                    padding: '20px',
                    margin: '24px 24px 0px',
                    borderRadius: '8px',
                    border: `1px solid ${theme.palette.neutral.borderColor}`,
                    backgroundColor: 'neutral.white',
                    overflowY: 'auto',
                }}
            >
                <Stepper
                    activeStep={
                        prospect?.prospectStatus
                            ? steps.indexOf(
                                  STATUS_OPTIONS?.find((st) => st.name === prospect?.prospectStatus)
                                      ?.label ?? '',
                              )
                            : 0
                    }
                    alternativeLabel
                    sx={{
                        '.MuiStepLabel-label.MuiStepLabel-alternativeLabel': {
                            marginTop: '4px',
                        },
                        '.MuiStepLabel-label.Mui-active': {
                            color: theme.palette.primary.main,
                        },
                    }}
                >
                    {steps.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    gap: '24px',
                    height: 'fit-content',
                    width: 'calc(100% - 48px)',
                    padding: '24px',
                }}
            >
                <Box
                    sx={{
                        borderRadius: '8px',
                        border: `1px solid ${theme.palette.neutral.borderColor}`,
                        backgroundColor: 'neutral.white',
                        padding: '24px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '18px',
                        height: 'fit-content',
                        width: '100%',
                    }}
                >
                    {renderProspectInformation()}
                    <Typography variant="h3">Pricing inputs</Typography>
                    {needsQuoteFormFields && isLoadingQuoteFields ? (
                        <Stack spacing={2}>
                            {Array.from({ length: 4 }, (_, index) => (
                                <Skeleton key={index} variant="rounded" height={42} />
                            ))}
                        </Stack>
                    ) : (
                        <>
                            {prospect.matterType === 'Sale' && (
                                <SalePricingInput
                                    prospect={prospect}
                                    quoteFormFields={quoteFormFields}
                                    handleValueChange={handleValueChange}
                                    readOnly
                                />
                            )}
                            {prospect.matterType === 'Purchase' && (
                                <PurchasePricingInput
                                    prospect={prospect}
                                    quoteFormFields={quoteFormFields}
                                    handleValueChange={handleValueChange}
                                    readOnly
                                />
                            )}
                            {prospect.matterType === 'Refinance' && (
                                <RefinancePricingInput
                                    prospect={prospect}
                                    handleValueChange={handleValueChange}
                                    readOnly
                                />
                            )}
                            {prospect.matterType === 'PrivateLender' && (
                                <PrivateLenderPricingInput
                                    prospect={prospect}
                                    handleValueChange={handleValueChange}
                                    readOnly
                                />
                            )}
                            {isLoadingQuoteFields ? (
                                <Stack spacing={2}>
                                    {Array.from({ length: 2 }, (_, index) => (
                                        <Skeleton key={index} variant="rounded" height={42} />
                                    ))}
                                </Stack>
                            ) : (
                                summaryDynamicFields.map((field, index) => (
                                    <Box
                                        sx={{
                                            order: field.questionSchema.order,
                                            display: field.isVisibleToUser ? 'block' : 'none',
                                        }}
                                        key={index}
                                    >
                                        <FormComponent
                                            fieldObj={field}
                                            lawFirmId={prospect.lawFirmId}
                                            shouldRenderAsterisk={false}
                                            labelStyles={{ color: theme.palette.neutral.N_500 }}
                                            tabindex={field.questionSchema.order}
                                            containerPadding="0px"
                                            readOnly
                                        />
                                    </Box>
                                ))
                            )}
                        </>
                    )}
                </Box>
                <Box
                    sx={{
                        borderRadius: '8px',
                        border: `1px solid ${theme.palette.neutral.borderColor}`,
                        backgroundColor: 'neutral.white',
                        padding: '24px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '18px',
                        height: 'fit-content',
                        width: '100%',
                    }}
                >
                    <Typography variant="h3">Prospect status</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: '24px' }}>
                        <FormControl fullWidth>
                            <InputLabel id="lawyer-approval-status">
                                Lawyer approval status
                            </InputLabel>
                            <Select
                                labelId="lawyer-approval-status"
                                label="Lawyer approval status"
                                name="lawyerApprovalStatus"
                                value={prospect.lawyerApprovalStatus}
                                onChange={(event) =>
                                    handleValueChange(event.target.name, event.target.value)
                                }
                            >
                                <MenuItem key="NotRequired" value="NotRequired">
                                    Not required
                                </MenuItem>
                                <MenuItem key="Approved" value="Approved">
                                    Approved
                                </MenuItem>
                                <MenuItem key="Rejected" value="Rejected">
                                    Rejected
                                </MenuItem>
                                <MenuItem key="Deactivated" value="Deactivated">
                                    Deactivated
                                </MenuItem>
                                <MenuItem key="PendingApproval" value="PendingApproval">
                                    Pending approval
                                </MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl fullWidth>
                            <InputLabel shrink>Client acceptance status</InputLabel>
                            <Select
                                labelId="client-acceptance-status"
                                label="Client acceptance status"
                                name={'clientAcceptanceStatus'}
                                value={
                                    prospect.clientAcceptanceStatus !== null
                                        ? prospect.clientAcceptanceStatus
                                        : 'Pending'
                                }
                                onChange={(event) =>
                                    handleValueChange(event.target.name, event.target.value)
                                }
                            >
                                <MenuItem key="Pending" value="Pending">
                                    Pending
                                </MenuItem>
                                <MenuItem key="Accepted" value="Accepted">
                                    Accepted
                                </MenuItem>
                                <MenuItem key="Expired" value="Expired">
                                    Expired
                                </MenuItem>
                                <MenuItem key="Declined" value="Declined">
                                    Declined
                                </MenuItem>
                                <MenuItem key="RequestACall" value="RequestACall">
                                    Requested a call
                                </MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: '24px' }}>
                        {quoteReminder && (
                            <Box width="100%" display="flex" gap="10px" alignItems="center">
                                <LexoSwitch
                                    name="quoteEmailReminder"
                                    checked={prospect.isQuoteReminderEnabled}
                                    onChange={() => handleReminderChange('quote')}
                                    disabled={prospect.clientAcceptanceStatus !== 'Pending'}
                                />
                                <Typography variant="body1">Email Reminder for Quote</Typography>
                            </Box>
                        )}
                        {intakeReminder && (
                            <Box width="100%" display="flex" gap="10px" alignItems="center">
                                <LexoSwitch
                                    name="intakeEmailReminder"
                                    checked={prospect.isIntakeReminderEnabled}
                                    onChange={() => handleReminderChange('intake')}
                                    disabled={prospect.prospectStatus === 'IntakeCompleted'}
                                />
                                <Typography variant="body1">Email Reminder for Intake</Typography>
                            </Box>
                        )}
                    </Box>
                    <Typography variant="h3">Pricing breakdown</Typography>
                    {prospect.matterType === 'Sale' && (
                        <SalePricingBreakdown
                            prospect={prospect}
                            handleValueChange={handleValueChange}
                        />
                    )}
                    {prospect.matterType === 'Purchase' && (
                        <PurchasePricingBreakdown
                            prospect={prospect}
                            handleValueChange={handleValueChange}
                        />
                    )}
                    {prospect.matterType === 'Refinance' && (
                        <RefinancePricingBreakdown
                            prospect={prospect}
                            handleValueChange={handleValueChange}
                        />
                    )}
                    {prospect.matterType === 'PrivateLender' && (
                        <PrivateLenderPricingBreakdown
                            prospect={prospect}
                            handleValueChange={handleValueChange}
                        />
                    )}
                    <Divider orientation="horizontal" />
                    <Button
                        disabled={
                            prospect.clientAcceptanceStatus === 'Accepted' || isPatchInProgress
                        }
                        variant={'contained'}
                        startIcon={<EmailOutlinedIcon />}
                        sx={{
                            width: 'fit-content',
                        }}
                        onClick={() => setIsSendQuoteModalOpen(true)}
                    >
                        Send quote
                    </Button>
                </Box>
            </Box>
            {isSendQuoteModalOpen && (
                <SendManualQuoteModal
                    prospectId={prospectId}
                    prospect={prospect}
                    onClose={() => setIsSendQuoteModalOpen(false)}
                />
            )}
        </Box>
    );
}
